import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useMemo, useState } from 'react'
import { Mail, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useAuth } from '@/hooks/useAuth'
import {
  listEmailTemplatesFn,
  sendTestEmailFn,
  type EmailTemplateSummary,
} from '@/lib/firebase/functions'

type Status =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'success'; subject: string; to: string }
  | { kind: 'error'; message: string }

const CATEGORY_LABEL: Record<string, string> = {
  client: 'Client',
  provider: 'Provider',
  admin: 'Admin',
  transactional: 'Transactional',
}

export function AdminEmailTemplatesPage() {
  const { user } = useAuth()
  const [templates, setTemplates] = useState<EmailTemplateSummary[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [recipient, setRecipient] = useState<string>('')
  const [status, setStatus] = useState<Status>({ kind: 'idle' })

  useEffect(() => {
    if (user?.email) setRecipient(user.email)
  }, [user?.email])

  useEffect(() => {
    let cancelled = false
    setLoadError(null)
    listEmailTemplatesFn({})
      .then((res) => {
        if (cancelled) return
        setTemplates(res.data.templates)
        setSelectedId((current) => current ?? res.data.templates[0]?.id ?? null)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setLoadError(formatUserFacingError(err, 'Failed to load templates.'))
      })
    return () => { cancelled = true }
  }, [])

  const selected = useMemo(
    () => templates?.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId],
  )

  async function handleSendTest() {
    if (!selected || !recipient) return
    setStatus({ kind: 'sending' })
    try {
      const res = await sendTestEmailFn({ templateId: selected.id, to: recipient })
      setStatus({ kind: 'success', subject: res.data.subject, to: recipient })
    } catch (err) {
      setStatus({
        kind: 'error',
        message: formatUserFacingError(err, 'Send failed.'),
      })
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Mail className="h-6 w-6" />
        <div>
          <h1 className="text-2xl font-semibold">Email Templates</h1>
          <p className="text-sm text-muted-foreground">
            Preview every transactional email the system can send, and send a test copy to yourself.
          </p>
        </div>
      </div>

      {loadError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>We couldn&apos;t load the email templates</AlertTitle>
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}

      {!templates && !loadError && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading templates…
        </div>
      )}

      {templates && (
        <div className="grid grid-cols-12 gap-6">
          {/* ── List ─────────────────────────────────────────────────── */}
          <Card className="col-span-12 lg:col-span-4">
            <CardHeader>
              <CardTitle className="text-base">Templates ({templates.length})</CardTitle>
              <CardDescription>Click any template to preview it.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ul className="divide-y">
                {templates.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedId(t.id)
                        setStatus({ kind: 'idle' })
                      }}
                      className={cn(
                        'w-full text-left px-4 py-3 transition hover:bg-muted/50',
                        selectedId === t.id && 'bg-muted',
                      )}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-medium text-sm">{t.name}</span>
                        <Badge variant="secondary" className="shrink-0">
                          {CATEGORY_LABEL[t.category] ?? t.category}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {t.description}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* ── Preview + send ───────────────────────────────────────── */}
          <div className="col-span-12 lg:col-span-8 space-y-4">
            {selected ? (
              <>
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <CardTitle className="text-base">{selected.name}</CardTitle>
                        <CardDescription className="mt-1">{selected.description}</CardDescription>
                      </div>
                      <Badge variant="outline">{CATEGORY_LABEL[selected.category] ?? selected.category}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">Trigger:</span> {selected.trigger}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Subject:</span>{' '}
                      <span className="font-mono">{selected.subject}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Send a test to yourself</CardTitle>
                    <CardDescription>
                      Renders the template with sample data and sends to one address. Subject is prefixed with <code>[TEST]</code>.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                      <div className="flex-1 space-y-1.5">
                        <Label htmlFor="test-recipient">Recipient</Label>
                        <Input
                          id="test-recipient"
                          type="email"
                          value={recipient}
                          onChange={(e) => setRecipient(e.target.value)}
                          placeholder="you@example.com"
                        />
                      </div>
                      <Button
                        onClick={handleSendTest}
                        disabled={!recipient || status.kind === 'sending'}
                        className="sm:w-auto"
                      >
                        {status.kind === 'sending' ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                        ) : (
                          <><Send className="h-4 w-4 mr-2" /> Send Test</>
                        )}
                      </Button>
                    </div>
                    {status.kind === 'success' && (
                      <Alert>
                        <CheckCircle2 className="h-4 w-4" />
                        <AlertTitle>Test sent</AlertTitle>
                        <AlertDescription>
                          “{status.subject}” → <span className="font-mono">{status.to}</span>
                        </AlertDescription>
                      </Alert>
                    )}
                    {status.kind === 'error' && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>We couldn&apos;t send the test email</AlertTitle>
                        <AlertDescription>{status.message}</AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Preview</CardTitle>
                    <CardDescription>Rendered with the template's sample data.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="rendered">
                      <TabsList>
                        <TabsTrigger value="rendered">Rendered</TabsTrigger>
                        <TabsTrigger value="html">HTML source</TabsTrigger>
                        <TabsTrigger value="data">Sample data</TabsTrigger>
                      </TabsList>
                      <TabsContent value="rendered">
                        <iframe
                          key={selected.id}
                          title={`Preview: ${selected.name}`}
                          srcDoc={selected.html}
                          sandbox=""
                          className="w-full h-[640px] rounded border bg-white"
                        />
                      </TabsContent>
                      <TabsContent value="html">
                        <pre className="text-xs bg-muted rounded p-3 overflow-auto max-h-[640px]">
                          <code>{selected.html}</code>
                        </pre>
                      </TabsContent>
                      <TabsContent value="data">
                        <pre className="text-xs bg-muted rounded p-3 overflow-auto max-h-[640px]">
                          <code>{selected.sampleDataJson}</code>
                        </pre>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  Select a template to preview it.
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
